import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import './ProductCarousel.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { StoreContext } from '../context/StoreContext';
import { FaPlus, FaCheck } from 'react-icons/fa';
import { API_BASE, mediaSrc } from '../utils/api';

const ProductCard = ({ product }) => {
  const { addToCart } = useContext(StoreContext);
  const [isAdded, setIsAdded] = useState(false);
  const isDisabled = product.tag === 'Tükendi';

  // Kart bir link; sepet butonu link'in içinde olduğundan tıklama yayılımı durdurulur.
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      addToCart(product);
      setIsAdded(true);
      setTimeout(() => setIsAdded(false), 1000);
    }
  };

  return (
    <Link to={`/store/${product.id}`} className="product-card">
      <div className="product-image-container">
        <img src={mediaSrc(product.image)} alt={product.name} className="product-image" loading="lazy" />
        {product.tag && (
          <span className={`product-tag ${product.tag === 'Tükendi' ? 'sold-out' : ''}`}>{product.tag}</span>
        )}
        <button
          onClick={handleAddToCart}
          className={`add-to-cart-btn ${isAdded ? 'added' : ''} ${isDisabled ? 'disabled' : ''}`}
          title={isDisabled ? 'Stokta Yok' : 'Sepete Ekle'}
          aria-label={isDisabled ? 'Stokta Yok' : 'Sepete Ekle'}
          disabled={isDisabled}
        >
          {isAdded ? <FaCheck /> : <FaPlus />}
        </button>
      </div>
      <div className="product-info">
        <p className="product-price">{product.price}</p>
        <h3 className="product-name">{product.name}</h3>
      </div>
    </Link>
  );
};

const PrevArrow = () => (
  <svg viewBox="0 0 40 14" xmlns="http://www.w3.org/2000/svg"><path d="M7.153 1.5l-5.656 5.5 5.656 5.5M1.5 7h37"/></svg>
);
const NextArrow = () => (
  <svg viewBox="0 0 40 14" xmlns="http://www.w3.org/2000/svg"><path d="M32.847 1.5l5.656 5.5-5.656 5.5M38.5 7h-37"/></svg>
);

const isMobile = () => window.innerWidth <= 768;
const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};

const ProductCarousel = () => {
  const carouselRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [mobile, setMobile] = useState(isMobile());
  const [products, setProducts] = useState([]);

  const isDragging = useRef(false);
  const dragMoved = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);
  const velocity = useRef(0);
  const animationFrameId = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/products`)
      .then((r) => r.json())
      .then((data) => { if (data.success) setProducts(data.items.slice(0, 8)); })
      .catch(() => {});
  }, []);

  const momentumLoop = useCallback(() => {
    carouselRef.current.scrollLeft += velocity.current;
    velocity.current *= 0.95;
    if (Math.abs(velocity.current) > 0.5) {
      animationFrameId.current = requestAnimationFrame(momentumLoop);
    }
  }, []);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragMoved.current = false;
    carouselRef.current.classList.add('is-dragging');
    startX.current = e.pageX - carouselRef.current.offsetLeft;
    scrollLeftRef.current = carouselRef.current.scrollLeft;
    velocity.current = 0;
    cancelAnimationFrame(animationFrameId.current);
  };

  /**
   * Kartlar artık ürün detayına giden birer <a>. Karuseli sürüklemek fareyi bir
   * kartın üzerinde bırakınca tarayıcı bunu tıklama sayıyor ve sürükleme sonunda
   * ürün sayfasına atlıyordu. Eşiği aşan bir sürüklemeden sonraki ilk click
   * yakalama fazında iptal edilir.
   */
  const handleClickCapture = useCallback((e) => {
    if (!dragMoved.current) return;
    dragMoved.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleMouseLeave = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    carouselRef.current.classList.remove('is-dragging');
    animationFrameId.current = requestAnimationFrame(momentumLoop);
  }, [momentumLoop]);
  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    carouselRef.current.classList.remove('is-dragging');
    animationFrameId.current = requestAnimationFrame(momentumLoop);
  }, [momentumLoop]);
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = x - startX.current;
    // 5px'ten fazla kaydırma gerçek bir sürüklemedir; sonrasındaki click yutulur.
    if (Math.abs(walk) > 5) dragMoved.current = true;
    const newScroll = scrollLeftRef.current - walk;
    velocity.current = newScroll - carouselRef.current.scrollLeft;
    carouselRef.current.scrollLeft = newScroll;
  };

  const handleScroll = () => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const maxScroll = scrollWidth - clientWidth;
    setProgress(maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0);
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.addEventListener('scroll', handleScroll, { passive: true });
    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mouseleave', handleMouseLeave);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('click', handleClickCapture, true);
    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mouseleave', handleMouseLeave);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('click', handleClickCapture, true);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [momentumLoop, handleMouseLeave, handleMouseUp, handleClickCapture, products]);

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scroll = (direction) => {
    if (!carouselRef.current) return;
    const card = carouselRef.current.querySelector('.product-card');
    if (!card) return;
    carouselRef.current.scrollBy({ left: direction * (card.offsetWidth + 20), behavior: 'smooth' });
  };

  if (products.length === 0) return null;

  return (
    <section className="product-carousel-section">
      <div className="carousel-header">
        <h2 className="carousel-title">ÜRÜNLERİMİZ</h2>
        <Link to="/store" className="store-link">MAĞAZAYI ZİYARET ET</Link>
      </div>
      <div className="carousel-container">
        {mobile ? (
          <>
            <Swiper spaceBetween={16} slidesPerView={1} style={{ marginBottom: 12 }}>
              {chunkArray(products.filter((_, i) => i % 2 === 0), 2).map((group, idx) => (
                <SwiperSlide key={idx}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {group.map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <Swiper spaceBetween={16} slidesPerView={1}>
              {chunkArray(products.filter((_, i) => i % 2 === 1), 2).map((group, idx) => (
                <SwiperSlide key={idx}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {group.map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        ) : (
          <>
            <button onClick={() => scroll(-1)} className="carousel-arrow prev"><PrevArrow /></button>
            <div className="carousel-track" ref={carouselRef}>
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
            <button onClick={() => scroll(1)} className="carousel-arrow next"><NextArrow /></button>
          </>
        )}
      </div>
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>
    </section>
  );
};

export default ProductCarousel;
